/**
 * ColumnBuilder Component
 *
 * Shared WYSIWYG column editor used by both CreateBoardForm (board creation)
 * and TemplateEditDialog (template editing/creation).
 *
 * Renders a horizontally scrolling container of EditableKanbanColumn components
 * with drag-and-drop reordering and an "Add Column" button.
 */

import { useState } from 'react';
import { Icon, IconName } from '@/icons';
import { useThemeColors } from '@/styles';
import { radius, spacing } from '@/styles/design-tokens';
import { EditableKanbanColumn } from './EditableKanbanColumn';
import { CreateBoardColumnsItem } from '@/types/generated/processor-types';

export interface ColumnBuilderProps {
  columns: CreateBoardColumnsItem[];
  onColumnsChange: (columns: CreateBoardColumnsItem[]) => void;
  /** Custom removal handler. If provided, called instead of default removal. */
  onRemove?: (index: number) => void;
  maxColumns?: number;
  disabled?: boolean;
  /** Label shown above the columns. Defaults to "Board Columns" */
  label?: string;
  /** Error message to display */
  error?: string;
}

export function ColumnBuilder({
  columns,
  onColumnsChange,
  onRemove,
  maxColumns = 20,
  disabled = false,
  label = 'Board Columns',
  error,
}: ColumnBuilderProps) {
  const colors = useThemeColors();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const handleColumnUpdate = (
    index: number,
    updated: CreateBoardColumnsItem
  ) => {
    const next = [...columns];
    next[index] = updated;
    onColumnsChange(next);
  };

  const handleColumnRemove = (index: number) => {
    if (onRemove) {
      onRemove(index);
      return;
    }
    if (columns.length > 1) {
      const next = columns.filter((_, i) => i !== index);
      next.forEach((col, i) => {
        col.position = i;
      });
      onColumnsChange(next);
    }
  };

  const handleAddColumn = () => {
    if (columns.length < maxColumns) {
      onColumnsChange([
        ...columns,
        {
          name: `Column ${columns.length + 1}`,
          position: columns.length,
          color: colors.medium,
        },
      ]);
    }
  };

  const handleDragStart = (index: number) => setDraggedIndex(index);

  const handleDragOver = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;
    const next = [...columns];
    const dragged = next[draggedIndex];
    next.splice(draggedIndex, 1);
    next.splice(index, 0, dragged);
    next.forEach((col, i) => {
      col.position = i;
    });
    onColumnsChange(next);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => setDraggedIndex(null);

  const atMax = columns.length >= maxColumns || disabled;

  return (
    <div>
      <div className="mb-3">
        <div className="flex items-center gap-2 mb-1">
          <h4
            className="font-semibold text-md"
            style={{ color: colors.text.primary }}
          >
            {label}
            <span
              className="ml-2 px-2 py-0.5 text-xs font-medium"
              style={{
                backgroundColor: colors.primary,
                color: colors.background,
                borderRadius: radius.sm,
              }}
            >
              {columns.length}
            </span>
          </h4>
        </div>
        <p className="text-xs" style={{ color: colors.text.secondary }}>
          Drag columns to reorder — hover to edit name, color, and rules
        </p>
      </div>

      {error && (
        <div className="text-xs mb-2" style={{ color: colors.error }}>
          {error}
        </div>
      )}

      {/* Horizontal scrolling columns container */}
      <div
        style={{
          display: 'flex',
          gap: spacing.md,
          overflowX: 'auto',
          overflowY: 'visible',
          padding: `${spacing.sm} 0`,
          marginBottom: spacing.md,
          height: 'calc(400px * var(--sizing-multiplier, 1))',
        }}
      >
        {columns.map((column, index) => (
          <EditableKanbanColumn
            key={index}
            column={column}
            index={index}
            totalColumns={columns.length}
            onUpdate={handleColumnUpdate}
            onRemove={handleColumnRemove}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            isDragging={draggedIndex === index}
          />
        ))}

        {/* Add Column button */}
        <button
          type="button"
          onClick={handleAddColumn}
          disabled={atMax}
          className="flex-shrink-0 h-full"
          style={{
            width: 'calc(120px * var(--sizing-multiplier, 1))',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.sm,
            padding: spacing.xl,
            backgroundColor: atMax ? colors.offWhite : colors.background,
            color: atMax ? colors.disabled.text : colors.text.secondary,
            border: `1px solid ${colors.border.default}`,
            borderRadius: radius.xl,
            cursor: atMax ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => {
            if (!atMax) {
              e.currentTarget.style.backgroundColor = colors.offWhite;
              e.currentTarget.style.borderColor = colors.primary;
              e.currentTarget.style.color = colors.primary;
            }
          }}
          onMouseLeave={e => {
            if (!atMax) {
              e.currentTarget.style.backgroundColor = colors.background;
              e.currentTarget.style.borderColor = colors.border.default;
              e.currentTarget.style.color = colors.text.secondary;
            }
          }}
        >
          <Icon name={IconName.PLUS} size="lg" />
          <span className="font-medium">Add Column</span>
          <span className="text-xs" style={{ color: colors.text.tertiary }}>
            {columns.length}/{maxColumns}
          </span>
        </button>
      </div>
    </div>
  );
}
